import { Box, Container, Stack, Typography, Link, IconButton, Divider } from "@mui/material";
import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';
import LocationOn from '@mui/icons-material/LocationOn';
import Email from '@mui/icons-material/Email';
import Phone from '@mui/icons-material/Phone';
import { NavLink } from "react-router-dom";
import { Routes } from '../routes/RouteConstants';
import { useTenant } from '../contexts/TenantContext';
import { tint } from '../utils/textStyles';
import { SECTION_SCROLL_MARGIN } from './layoutMetrics';

// "06 30 452 8948" as a number a phone can dial from abroad too: +36304528948.
const telHref = (phoneNumber) => {
  const digits = phoneNumber.replace(/[^\d+]/g, '');
  return `tel:${digits.startsWith('06') ? `+36${digits.slice(2)}` : digits}`;
};

const mapsHref = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

/**
 * One way to reach the shop: the icon in the same light badge the profile card's contact rows
 * use, a small label, and the value itself as the link that acts on it.
 */
const ContactItem = ({ icon, label, value, href, external, textColor, iconColor }) => (
  <Stack direction="row" spacing={1.75} alignItems="center" minWidth={0}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: iconColor,
        backgroundColor: tint(iconColor, 0.12),
        border: `1px solid ${tint(iconColor, 0.3)}`,
      }}
    >
      {icon}
    </Box>

    <Box minWidth={0}>
      <Typography fontSize={11} fontWeight={600} textTransform="uppercase" letterSpacing={1}
        sx={{ color: tint(textColor, 0.6) }}>
        {label}
      </Typography>
      <Link
        href={href}
        color="inherit"
        underline="hover"
        fontSize={16}
        fontWeight={600}
        noWrap
        display="block"
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {value}
      </Link>
    </Box>
  </Stack>
);

// In the footer's icon colour like the contact badges, so every icon down here is one colour.
const SocialButton = ({ href, label, children, iconColor, background }) => (
  <IconButton
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    sx={{
      width: 40,
      height: 40,
      color: iconColor,
      border: `1px solid ${tint(iconColor, 0.4)}`,
      transition: 'background-color 200ms ease, color 200ms ease, border-color 200ms ease',
      '&:hover': {
        backgroundColor: iconColor,
        borderColor: iconColor,
        color: background,
      },
    }}
  >
    {children}
  </IconButton>
);

/**
 * Two tiers. The upper one is the tenant's own footer colours: the business name with its
 * social links, then the three ways to reach it — this is the "Kapcsolatok" the header links
 * to. The lower one is the header's bar colour again, so the page closes on the colour it
 * opened with: copyright on one side, the legal pages on the other.
 */
export default function Footer() {
  const { tenant, displayConfig, homePage } = useTenant();
  const textColor = displayConfig?.footerColor;
  const iconColor = displayConfig?.footerIconColor || textColor;
  // The bottom strip wears the header's colours, so its ink is the header's too.
  const barColor = displayConfig?.headerColor || '#fff';
  const businessName = homePage?.businessName || tenant?.name;
  const hasSocials = displayConfig?.instagramUrl || displayConfig?.facebookUrl;

  return (
    <Box component="footer" sx={{ background: displayConfig?.footerBackground, color: textColor }}>
      <Container
        id="contacts"
        maxWidth="xl"
        sx={{ scrollMarginTop: SECTION_SCROLL_MARGIN, py: { xs: 3, md: 3.5 } }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography fontSize={{ xs: 20, md: 22 }} fontWeight={800} lineHeight={1.2}>
            {businessName}
          </Typography>

          {hasSocials && (
            <Stack direction="row" spacing={1.25}>
              {displayConfig?.instagramUrl && (
                <SocialButton href={displayConfig.instagramUrl} label="Instagram"
                  iconColor={iconColor} background={displayConfig?.footerBackground}>
                  <Instagram fontSize="small" />
                </SocialButton>
              )}
              {displayConfig?.facebookUrl && (
                <SocialButton href={displayConfig.facebookUrl} label="Facebook"
                  iconColor={iconColor} background={displayConfig?.footerBackground}>
                  <Facebook fontSize="small" />
                </SocialButton>
              )}
            </Stack>
          )}
        </Stack>

        <Divider sx={{ my: { xs: 2, md: 2.5 }, borderColor: tint(textColor, 0.18) }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {displayConfig?.address && (
            <ContactItem
              icon={<LocationOn />}
              label="Cím"
              value={displayConfig.address}
              href={mapsHref(displayConfig.address)}
              external
              textColor={textColor}
              iconColor={iconColor}
            />
          )}
          {displayConfig?.email && (
            <ContactItem
              icon={<Email />}
              label="Email"
              value={displayConfig.email}
              href={`mailto:${displayConfig.email}`}
              textColor={textColor}
              iconColor={iconColor}
            />
          )}
          {displayConfig?.phoneNumber && (
            <ContactItem
              icon={<Phone />}
              label="Telefon"
              value={displayConfig.phoneNumber}
              href={telHref(displayConfig.phoneNumber)}
              textColor={textColor}
              iconColor={iconColor}
            />
          )}
        </Box>
      </Container>

      <Box sx={{ backgroundColor: displayConfig?.headerBackground || '#000', color: barColor }}>
        <Container maxWidth="xl">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            py={1.5}
          >
            <Typography variant="body2" sx={{ color: tint(barColor, 0.65) }} textAlign="center">
              © {new Date().getFullYear()} {tenant?.name}. Minden jog fenntartva.
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              divider={<Box component="span" sx={{ color: tint(barColor, 0.35) }}>·</Box>}
              sx={{
                '& a': {
                  color: tint(barColor, 0.8),
                  textDecoration: 'none',
                  fontSize: 14,
                  transition: 'color 200ms ease',
                },
                '& a:hover': { color: barColor, textDecoration: 'underline', textUnderlineOffset: '3px' },
              }}
            >
              <NavLink to={Routes.PRIVAY_POLICY}>Adatkezelési nyilatkozat</NavLink>
              <NavLink to={Routes.TERMS_AND_CONDITIONS}>ÁSZF</NavLink>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  )
}
